import { useState, useEffect } from "react";
import api from '../api/axios';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../context/LanguageContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaUser, FaCheck, FaTimes } from "react-icons/fa";
import Select from "react-select";
import '../styles/common.css';
import './AjouterUser.css';

export default function AjouterUser() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [groupPermissions, setGroupPermissions] = useState([]); // Permissions des groupes sélectionnés
  const [selectedPermissions, setSelectedPermissions] = useState([]); // Permissions cochées par l'utilisateur
  const [loading, setLoading] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    email: "",
    first_name: "",
    last_name: "",
    groups: [],
    is_staff: false,
    is_superuser: false,
  });
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [groupsError, setGroupsError] = useState("");

  // Charger la liste des groupes
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const groupsRes = await api.get("/api/groups/");
        const groupsData = Array.isArray(groupsRes.data) 
          ? groupsRes.data 
          : (groupsRes.data.results || []);
        setGroups(groupsData);
      } catch (err) {
        console.error("Erreur lors du chargement des groupes:", err);
      }
    };
    fetchGroups();
  }, []);

  // Charger les permissions des groupes sélectionnés
  useEffect(() => {
    const fetchGroupPermissions = async () => {
      if (formData.groups.length === 0) {
        setGroupPermissions([]);
        setSelectedPermissions([]);
        return;
      }

      setLoadingPermissions(true);
      try {
        // Charger les détails de chaque groupe sélectionné
        const groupDetailsPromises = formData.groups.map(groupId =>
          api.get(`/api/groups/${groupId}/`).catch(() => null)
        );
        
        const groupDetails = await Promise.all(groupDetailsPromises);
        
        // Collecter toutes les permissions uniques de tous les groupes
        // Format backend: { id, codename, full_codename, name }
        const allPermsMap = new Map(); // Utiliser Map pour éviter les doublons et garder les infos complètes
        groupDetails.forEach((groupDetail) => {
          if (groupDetail && groupDetail.data) {
            const group = groupDetail.data;
            // Les permissions sont dans le champ 'permissions' selon la doc backend
            const perms = group.permissions || [];
            perms.forEach(perm => {
              // Prioriser full_codename (ex: "core.add_user"), sinon codename (ex: "add_user")
              const permKey = perm.full_codename || perm.codename || perm.name || perm;
              
              if (!allPermsMap.has(permKey)) {
                // Utiliser le nom du backend si disponible, sinon générer à partir du codename
                const permName = perm.name || 
                  (permKey.replace(/\./g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
                
                allPermsMap.set(permKey, {
                  codename: permKey, // Utiliser full_codename en priorité pour l'ID unique
                  name: permName,
                  id: perm.id, // Garder l'ID si disponible
                });
              }
            });
          }
        });

        // Convertir la Map en tableau
        const permissionsArray = Array.from(allPermsMap.values());

        setGroupPermissions(permissionsArray);
        // Pré-cocher toutes les permissions disponibles pour ces groupes
        setSelectedPermissions(permissionsArray.map(p => p.codename));
      } catch (err) {
        console.error("Erreur lors du chargement des permissions des groupes:", err);
        setGroupPermissions([]);
        setSelectedPermissions([]);
      } finally {
        setLoadingPermissions(false);
      }
    };

    fetchGroupPermissions();
  }, [formData.groups]);

  const validateEmail = (email) => {
    if (!email || email.trim() === "") {
      return "L'email est obligatoire";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return "Veuillez entrer une adresse email valide";
    }
    return "";
  };

  const validatePassword = (password, confirmPassword) => {
    if (!password || password.trim() === "") {
      return "Le mot de passe est obligatoire";
    }
    if (password.length < 8) {
      return "Le mot de passe doit contenir au moins 8 caractères";
    }
    if (confirmPassword && password !== confirmPassword) {
      return "Les mots de passe ne correspondent pas";
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    
    if (name === "email") {
      const error = validateEmail(value);
      setEmailError(error);
    }
    
    if (name === "password" || name === "confirmPassword") {
      const error = validatePassword(
        name === "password" ? value : formData.password,
        name === "confirmPassword" ? value : formData.confirmPassword
      );
      setPasswordError(error);
    }

    // Si is_superuser est coché, cocher aussi is_staff automatiquement
    if (name === "is_superuser" && checked) {
      setFormData(prev => ({
        ...prev,
        is_superuser: checked,
        is_staff: true,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const emailValidationError = validateEmail(formData.email);
    setEmailError(emailValidationError);
    
    const passwordValidationError = validatePassword(formData.password, formData.confirmPassword);
    setPasswordError(passwordValidationError);
    
    if (emailValidationError || passwordValidationError || !formData.username || !formData.password) {
      if (passwordValidationError) {
        toast.error(passwordValidationError);
      } else {
        toast.error("Veuillez remplir tous les champs obligatoires");
      }
      return;
    }
    
    setLoading(true);
    try {
      // Calculer les permissions exclues (décochées) : toutes les permissions des groupes moins celles cochées
      const allGroupPermissionIds = groupPermissions
        .filter(perm => perm.id) // Garder seulement celles qui ont un ID
        .map(perm => perm.id);
      
      const selectedPermissionIds = selectedPermissions
        .map(permCodename => {
          // Trouver la permission correspondante pour récupérer son ID
          const perm = groupPermissions.find(p => p.codename === permCodename);
          return perm?.id;
        })
        .filter(id => id !== undefined); // Retirer les undefined
      
      const excludedPermissionIds = allGroupPermissionIds.filter(
        id => !selectedPermissionIds.includes(id)
      );

      // Détecter si le groupe "Administrateur" est sélectionné
      const adminGroup = groups.find(g => 
        g.name?.toLowerCase().includes('admin') || 
        g.name?.toLowerCase() === 'administrateur' ||
        g.id === 1 // ID du groupe Administrateur
      );
      const isAdmin = adminGroup && formData.groups.includes(adminGroup.id);

      // Validation des champs obligatoires
      if (!formData.groups || formData.groups.length === 0) {
        setGroupsError("Veuillez sélectionner au moins un groupe");
        toast.error("Veuillez sélectionner au moins un groupe");
        setLoading(false);
        return;
      } else {
        setGroupsError("");
      }
      
      if (!formData.email || !formData.first_name || !formData.last_name) {
        toast.error("Veuillez remplir tous les champs obligatoires");
        setLoading(false);
        return;
      }

      const dataToSubmit = {
        username: formData.username,
        password: formData.password,
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        groups: formData.groups,
        excluded_permissions: excludedPermissionIds, // IDs des permissions décochées
        // Si groupe Administrateur : is_superuser = true
        is_superuser: isAdmin ? true : false,
        is_staff: formData.is_staff || false,
      };
      
      await api.post("/api/users/", dataToSubmit);
      toast.success("Utilisateur créé avec succès !");
      
      // Navigate after a short delay to allow the toast to be seen
      setTimeout(() => {
        navigate("/users");
      }, 500);
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message ||
                          Object.values(err.response?.data || {}).flat().join(", ") ||
                          "Erreur lors de la création de l'utilisateur";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour extraire le nom du module depuis un codename
  const extractModuleName = (codename) => {
    // Si format "app.action_model" (ex: "core.add_animal")
    if (codename.includes('.')) {
      const parts = codename.split('.');
      if (parts.length >= 2) {
        // Prendre la partie après l'action (ex: "add_animal" -> "animal")
        const actionModel = parts[1];
        // Extraire le modèle en enlevant les préfixes d'actions
        const modelName = actionModel
          .replace(/^(add|change|delete|view)_/, '')
          .replace(/_/g, ' ');
        return modelName.charAt(0).toUpperCase() + modelName.slice(1);
      }
      return parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    }
    
    // Si format "action_model" (ex: "add_animal")
    const modelName = codename
      .replace(/^(add|change|delete|view)_/, '')
      .replace(/_/g, ' ');
    return modelName.charAt(0).toUpperCase() + modelName.slice(1);
  };

  // Fonction pour formater le nom d'une permission
  const formatPermissionName = (codename) => {
    // Si format "app.action_model"
    if (codename.includes('.')) {
      const parts = codename.split('.');
      if (parts.length >= 2) {
        const action = parts[1].split('_')[0]; // "add", "change", etc.
        const model = parts[1].replace(/^(add|change|delete|view)_/, '').replace(/_/g, ' ');
        const actionLabels = {
          'add': 'Ajouter',
          'change': 'Modifier',
          'delete': 'Supprimer',
          'view': 'Voir',
        };
        return `${actionLabels[action] || action} ${model}`;
      }
    }
    
    // Si format "action_model"
    const parts = codename.split('_');
    if (parts.length >= 2) {
      const action = parts[0];
      const model = parts.slice(1).join(' ');
      const actionLabels = {
        'add': 'Ajouter',
        'change': 'Modifier',
        'delete': 'Supprimer',
        'view': 'Voir',
      };
      return `${actionLabels[action] || action} ${model}`;
    }
    
    return codename.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Organiser les permissions des groupes sélectionnés par module
  const organizedPermissions = groupPermissions.reduce((acc, perm) => {
    const codename = perm.codename || perm.name || perm;
    const moduleName = extractModuleName(codename);
    
    if (!acc[moduleName]) acc[moduleName] = [];
    acc[moduleName].push({
      codename: codename,
      name: formatPermissionName(codename),
    });
    return acc;
  }, {});

  // Vérifier si le groupe Administrateur est sélectionné
  const adminGroup = groups.find(g => 
    g.name?.toLowerCase().includes('admin') || 
    g.name?.toLowerCase() === 'administrateur' ||
    g.id === 1 // ID du groupe Administrateur
  );
  const isAdminSelected = adminGroup && formData.groups.includes(adminGroup.id);

  return (
    <div className="ajouter-user-page">
      <div className="title-badge">
        <FaUser className="page-title-icon" aria-hidden="true" />
        <span>{t.addUser || "Ajouter un utilisateur"}</span>
      </div>

      <form onSubmit={handleSubmit} className="user-form">
        <div className="form-info-banner">
          <span className="info-icon">ℹ️</span>
          <div className="info-content">
            <strong>{t.requiredFields}</strong>
            <span className="required-indicator">*</span> {t.requiredFieldNote}
          </div>
        </div>

        <div className="form-section-container">
          <h3 className="form-section-title">{t.basicInformation}</h3>
          
          <div className="form">
            <label htmlFor="username">
              {t.username} <span className="required-indicator">*</span>
            </label>
            <input 
              type="text" 
              id="username" 
              name="username" 
              value={formData.username} 
              onChange={handleChange} 
              required 
            />
          </div>

          <div className="form">
            <label htmlFor="password">
              Mot de passe <span className="required-indicator">*</span>
            </label>
            <input 
              type="password" 
              id="password" 
              name="password" 
              value={formData.password} 
              onChange={handleChange}
              onBlur={(e) => {
                const error = validatePassword(formData.password, formData.confirmPassword);
                setPasswordError(error);
              }}
              className={passwordError ? "input-error" : ""}
              required 
            />
            {passwordError && (
              <span className="field-error-message">{passwordError}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="confirmPassword">
              Confirmer le mot de passe <span className="required-indicator">*</span>
            </label>
            <input 
              type="password" 
              id="confirmPassword" 
              name="confirmPassword" 
              value={formData.confirmPassword} 
              onChange={handleChange}
              onBlur={(e) => {
                const error = validatePassword(formData.password, formData.confirmPassword);
                setPasswordError(error);
              }}
              className={passwordError ? "input-error" : ""}
              required 
            />
            {passwordError && (
              <span className="field-error-message">{passwordError}</span>
            )}
          </div>
          
          <div className="form">
            <label htmlFor="email">
              Email <span className="required-indicator">*</span>
            </label>
            <input 
              type="email" 
              id="email" 
              name="email" 
              value={formData.email || ""} 
              onChange={handleChange}
              onBlur={(e) => {
                const error = validateEmail(e.target.value);
                setEmailError(error);
              }}
              className={emailError ? "input-error" : ""}
              required
            />
            {emailError && (
              <span className="field-error-message">{emailError}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="first_name">
              Prénom <span className="required-indicator">*</span>
            </label>
            <input 
              type="text" 
              id="first_name" 
              name="first_name" 
              value={formData.first_name || ""} 
              onChange={handleChange}
              required
            />
          </div>

          <div className="form">
            <label htmlFor="last_name">
              Nom <span className="required-indicator">*</span>
            </label>
            <input 
              type="text" 
              id="last_name" 
              name="last_name" 
              value={formData.last_name || ""} 
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="form-section-container">
          <h3 className="form-section-title">Groupes</h3>
          
          <div className="form">
            <label htmlFor="groups">
              Groupes <span className="required-indicator">*</span>
            </label>
            <Select
              name="groups"
              inputId="groups"
              isMulti
              value={formData.groups.map(groupId => {
                const group = groups.find(g => g.id === groupId);
                return group ? { value: group.id, label: group.name } : null;
              }).filter(Boolean)}
              onChange={(selectedOptions) => {
                const groupIds = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                setFormData({ ...formData, groups: groupIds });
                // Clear error when groups are selected
                if (groupIds.length > 0) {
                  setGroupsError("");
                }
                // Les permissions seront automatiquement rechargées via useEffect
              }}
              options={groups.map(group => ({
                value: group.id,
                label: group.name
              }))}
              classNamePrefix="react-select"
              placeholder={t.selectGroups}
            />
            {groupsError && (
              <span className="field-error-message">{groupsError}</span>
            )}
          </div>

          {isAdminSelected && (
            <div className="form">
              <label htmlFor="is_staff" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  id="is_staff"
                  name="is_staff"
                  checked={formData.is_staff || false}
                  onChange={handleChange}
                  style={{ 
                    width: '18px', 
                    height: '18px', 
                    cursor: 'pointer', 
                    margin: 0,
                    appearance: 'checkbox',
                    WebkitAppearance: 'checkbox',
                    MozAppearance: 'checkbox'
                  }}
                />
                <span>Autoriser à créer d'autres utilisateurs (Staff)</span>
              </label>
            </div>
          )}
        </div>

        <div className="form-section-container">
          <h3 className="form-section-title">Permissions</h3>
          <p className="form-section-description">
            Les permissions ci-dessous proviennent des groupes sélectionnés. Décoche ce que tu ne veux pas attribuer à l'utilisateur.
          </p>
          
          <div className="permissions-container">
            {loadingPermissions ? (
              <p className="no-permissions-message">Chargement des permissions...</p>
            ) : formData.groups.length === 0 ? (
              <p className="no-permissions-message">
                Sélectionnez d'abord un ou plusieurs groupes pour voir leurs permissions.
              </p>
            ) : Object.keys(organizedPermissions).length > 0 ? (
              Object.entries(organizedPermissions)
                .sort(([a], [b]) => a.localeCompare(b)) // Trier par ordre alphabétique
                .map(([module, perms]) => (
                <div key={module} className="permission-group">
                  <h4 className="permission-group-title">{module}</h4>
                  <div className="permissions-checkboxes">
                    {perms.map((perm) => (
                      <label key={perm.codename} className="permission-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedPermissions.includes(perm.codename)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedPermissions(prev => [...prev, perm.codename]);
                            } else {
                              setSelectedPermissions(prev => prev.filter(p => p !== perm.codename));
                            }
                          }}
                        />
                        <span>{perm.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <p className="no-permissions-message">
                Les groupes sélectionnés n'ont pas de permissions assignées.
              </p>
            )}
          </div>
        </div>

        <div className="actions">
          <button type="submit" className="save-but" disabled={loading}>
            <FaCheck />
            <span>{loading ? "Création..." : "Créer l'utilisateur"}</span>
          </button>
          <button 
            type="button" 
            className="cancel-but" 
            onClick={() => navigate("/users")}
            disabled={loading}
          >
            <FaTimes />
            <span>{t.cancel || "Annuler"}</span>
          </button>
        </div>
      </form>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}
